import ReactCountryFlag from "react-country-flag";
import { Path, PathValue } from "react-hook-form";

import { VatPrefixInfo } from "@/redux/modules/api/data/vatPrefixes/vatPrefixes.types";
import { getCountryVatMask } from "@/services/vatCalculation.service.RHF";

import { BaseFieldProps } from "../BaseField/BaseField.component";
import { FormFieldProps, isFormRequired } from "../formField.types";
import { PrefixedInputField } from "../PrefixedInput/prefixedInput.component";
import { BaseOption, LabelType } from "../SelectField/selectField.component";
import { VatFieldValues } from "./vatField.schema";
import { VatInfoHandler } from "./vatInfo.component";

type VatFieldProperties = {
    disabled?: boolean;
    vatPrefixes: Array<VatPrefixInfo>;
};

export type VatFieldProps<T extends VatFieldValues> = FormFieldProps<
    Omit<BaseFieldProps<VatFieldValues, "vatInfo.vatNr">, "name" | "required"> &
        VatFieldProperties & {
            required: isFormRequired<T, Extract<Path<T>, "vatInfo.vatNr">>;
        },
    T
>;

type VatPrefixOption = BaseOption<
    PathValue<VatFieldValues, Path<VatFieldValues>>
> & {
    isoCode?: string;
};

const VatField = <T extends VatFieldValues = never>({
    label,
    required,
    test,
    disabled,
    vatPrefixes
}: VatFieldProps<T>) => {
    if (!vatPrefixes?.length) {
        return null;
    }

    const options: Array<VatPrefixOption> = vatPrefixes.map((item) => {
        return {
            isoCode: item.country.isoCode,
            label: item.country.name,
            value: item.prefix
        };
    });

    if (options.length > 0) {
        options.splice(5, 0, {
            label: LabelType.DIVIDER,
            isDisabled: true
        });
    }

    const renderOption = (option: VatPrefixOption) => {
        if (option.label === LabelType.DIVIDER) {
            return <hr />;
        }

        if (!option.isoCode) {
            return null;
        }

        return (
            <div className="text-nowrap">
                <ReactCountryFlag
                    svg
                    countryCode={option.isoCode}
                    style={{
                        width: "24px",
                        height: "24px"
                    }}
                />
                <span className="pl-2">{option.isoCode}</span>
            </div>
        );
    };

    return (
        <>
            <PrefixedInputField<VatFieldValues, VatPrefixOption>
                test={test}
                name={"vatInfo.vatNr"}
                prefixName={"vatInfo.vatNrPrefix"}
                label={label}
                prefixOptions={options}
                required={required}
                inputMaskBasedOnPrefix={getCountryVatMask}
                renderOption={renderOption}
                disabled={disabled}
            />
            <VatInfoHandler />
        </>
    );
};

export { VatField };
